import React, { useEffect, useId, useRef, useState } from 'react';

// Modal / hoja inferior compartida de VLS. Porta la mecánica de
// accesibilidad de vlscr-hub (Escape, click en fondo, bloqueo de scroll,
// foco) mejorada a un focus-trap real (mismo mecanismo que ConfirmDialog),
// y el shell visual de NewVisitModal del CRM (hoja inferior con handle,
// header y footer fijos, cuerpo con scroll propio, safe-area) — resuelve
// el scroll anidado y los botones de cerrar de 18-32px de la auditoría.
//
// Dos modos:
//  - CON `title`: arma su propio header fijo (título + cerrar 44px) y dedica
//    `children` al cuerpo, que scrollea solo. Con `footer` agrega un pie
//    fijo con safe-area (los botones de acción no se pierden en un
//    formulario largo). Modo recomendado para modales nuevos.
//  - SIN `title`: legado, `children` es el contenido completo (header
//    incluido, con su propio `sticky top-0`) — el mismo contrato que tenía
//    el Modal original de vlscr-hub, para no romper sus llamadas existentes.
//
// `zIndexClass` (por defecto z-50) fija la capa del contenedor, para apps cuyo
// header fijo va por encima de z-50 (rrhh pasa z-modal). Un control del cuerpo
// con `data-autofocus` recibe el foco inicial en vez del primero del DOM.
// `dismissible` (default true) retrocompatible: en `false` no cierra con
// clic en el fondo ni con Escape, para flujos que exigen una decisión
// explícita (ej. calculadoras con cálculo en curso). `cerrarConFondo`
// (default true) en `false` deja el fondo inerte pero Escape y la X siguen
// cerrando: para formularios, donde un toque fuera (en el teléfono, la franja
// sobre la hoja) descartaba lo escrito (auditoría de UI 2026-09-25, rrhh #6).
// La X del header solo
// se oculta si además no hay `onClose` — con `onClose` presente el cierre
// sigue siendo explícito y la X se mantiene.
// Alto realmente visible de la pantalla. Con el teclado en pantalla abierto,
// `innerHeight` (el viewport de layout) NO cambia: el que se achica es
// `visualViewport`. Un modal anclado abajo con `items-end` queda entonces
// DEBAJO del teclado, y el `max-h-[90dvh]` se calcula sobre una pantalla que
// ya no existe: en la auditoría móvil de horario (2026-09-19, H3) el cuerpo
// del modal de jornada quedaba en ~30 px y el título se iba fuera de la
// pantalla. Con este dato el contenedor se pega al viewport visible y el
// contenido útil queda arriba del teclado.
// Relleno horizontal en píxeles fijos (px-[16px] sm:px-[20px]), no px-4 sm:px-5:
// trece apps redefinen la escala de spacing al doble (px-4 = 32 px) y en ellas
// cada hoja perdía 64 px de ancho en el teléfono, con más relleno en el
// teléfono que en la computadora (auditoría de UI 2026-09-25, horario #3).
// `corto` (< 500 px) es la señal de "teclado abierto o pantalla muy baja":
// ahí se esconde la manija y el encabezado se compacta todavía más.
function useVistaVisible(activo) {
  const leer = () => {
    if (typeof window === 'undefined') return null;
    const vv = window.visualViewport;
    if (!vv) return null;
    return { alto: vv.height, arriba: vv.offsetTop };
  };
  const [vista, setVista] = useState(null);
  useEffect(() => {
    if (!activo || typeof window === 'undefined' || !window.visualViewport) {
      setVista(null);
      return undefined;
    }
    const vv = window.visualViewport;
    const on = () => setVista(leer());
    on();
    vv.addEventListener('resize', on);
    vv.addEventListener('scroll', on);
    return () => {
      vv.removeEventListener('resize', on);
      vv.removeEventListener('scroll', on);
    };
  }, [activo]);
  return { vista, corto: !!vista && vista.alto < 500 };
}

// Pila compartida de overlays abiertos (Modal y ConfirmDialog, en todas sus
// copias): solo el de arriba atiende Escape y Tab. Antes los dos escuchaban
// Escape en document y ninguno detenía el evento: Escape en una confirmación
// cerraba también el formulario de abajo y se perdían los cambios (auditoría
// de UI 2026-09-25, rrhh #2). Vive en globalThis porque son dos módulos.
// El orden de la pila es el de apertura: el overlay de arriba tiene que
// abrirse después del de abajo (no en el mismo render), como en todos los
// flujos de hoy.
function pilaOverlays() {
  if (!globalThis.__vlsPilaOverlays) globalThis.__vlsPilaOverlays = [];
  return globalThis.__vlsPilaOverlays;
}

// Mete el overlay en la pila mientras está abierto; devuelve si es el de arriba.
function usePilaOverlay(open) {
  const token = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const propio = {};
    token.current = propio;
    pilaOverlays().push(propio);
    return () => {
      const pila = pilaOverlays();
      const i = pila.indexOf(propio);
      if (i >= 0) pila.splice(i, 1);
    };
  }, [open]);
  return () => {
    const pila = pilaOverlays();
    return pila.length > 0 && pila[pila.length - 1] === token.current;
  };
}

export default function Modal({
  open,
  onClose,
  title,
  labelledBy,
  ariaLabel,
  footer,
  maxWidth = 'max-w-md',
  className = '',
  zIndexClass = 'z-50',
  dismissible = true,
  cerrarConFondo = true,
  children,
}) {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);
  const titleId = useId();
  // onClose casi siempre llega como arrow function inline (nueva referencia
  // en cada render del padre) — si el efecto de abajo dependiera de onClose,
  // cualquier re-render del padre mientras el modal está abierto (ej. cada
  // tecla en un input controlado) reiniciaba el focus-trap: reenfocaba el
  // elemento previo y volvía a auto-enfocar el primer control (el botón de
  // cerrar), robándose el foco del campo en el que se estaba escribiendo.
  // Guardar la versión más reciente en un ref deja que el efecto dependa
  // solo de `open` y siga leyendo el onClose actual para Escape/backdrop.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  // Mismo motivo que onCloseRef: dismissible casi siempre llega fijo, pero
  // se lee en un efecto que solo depende de `open`.
  const dismissibleRef = useRef(dismissible);
  useEffect(() => { dismissibleRef.current = dismissible; }, [dismissible]);
  const { vista, corto } = useVistaVisible(open);
  const esDeArriba = usePilaOverlay(open);

  // Con el teclado abierto el campo enfocado puede quedar tapado por él (el
  // navegador solo garantiza que se vea si el scroll es de la página, y acá
  // el que scrollea es el cuerpo del modal). Se lo centra a mano.
  useEffect(() => {
    if (!open) return undefined;
    const panel = panelRef.current;
    if (!panel) return undefined;
    const alEnfocar = (e) => {
      const el = e.target;
      if (!el?.matches?.('input, textarea, select, [contenteditable="true"]')) return;
      // Después del reflow del teclado, no en el mismo cuadro del foco.
      setTimeout(() => el.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 180);
    };
    panel.addEventListener('focusin', alEnfocar);
    return () => panel.removeEventListener('focusin', alEnfocar);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    const panel = panelRef.current;
    const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const getFocusables = () => Array.from(panel?.querySelectorAll(FOCUSABLE) || []);
    // En un modal que arranca con un buscador, el primer control es la X de
    // cerrar: data-autofocus deja elegir el campo que recibe el foco.
    const focusTimer = setTimeout(() => {
      const preferido = panel?.querySelector('[data-autofocus]');
      const [first] = getFocusables();
      (preferido || first || panel)?.focus?.();
    }, 0);
    function onKeyDown(e) {
      if (!esDeArriba()) return;
      if (e?.key === 'Escape') { if (dismissibleRef.current) onCloseRef.current?.(); return; }
      if (e?.key !== 'Tab') return;
      const focusables = getFocusables();
      if (focusables.length === 0) return;
      const first = focusables[0]; const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const resolvedLabelledBy = title ? titleId : labelledBy;
  // dvh, no vh: en móvil, vh se calcula como si la barra de direcciones
  // estuviera siempre oculta, así que con la barra visible el modal se
  // arma más alto de lo que realmente cabe y el fondo termina pegado
  // contra la barra — dvh sí sigue el viewport visible real. El modo con
  // título ya usaba dvh; este (legado, sin título — el que usa ChooserCard)
  // se había quedado con vh.
  const shellClasses = title
    ? 'max-h-[90dvh] overflow-hidden flex flex-col'
    : 'max-h-[85dvh] overflow-y-auto pb-[env(safe-area-inset-bottom)] sm:pb-0';
  // Cuando se conoce el viewport visible, el contenedor deja de ser
  // `inset-0` (la pantalla entera, teclado incluido) y se recorta a la parte
  // que de verdad se ve; el `max-h` en dvh pasa a medirse contra eso.
  const estiloContenedor = vista
    ? { margin: 0, top: vista.arriba, height: vista.alto, bottom: 'auto' }
    : { margin: 0 };
  const estiloShell = vista ? { maxHeight: '100%' } : undefined;

  return (
    <div
      className={`fixed inset-0 ${zIndexClass} flex items-end sm:items-center justify-center p-0 sm:p-4`}
      // Margen en 0 en línea: si el modal se monta dentro de un contenedor con
      // space-y-*, esa regla le pone margin-top al contenedor fijo y lo corre
      // hacia abajo, debajo de un header fijo (pasó en rrhh con 48 px).
      style={estiloContenedor}
      onMouseDown={(e) => { if (dismissible && cerrarConFondo && e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="absolute inset-0 bg-black/40 pointer-events-none" aria-hidden="true" />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={resolvedLabelledBy}
        aria-label={resolvedLabelledBy ? undefined : ariaLabel}
        onMouseDown={(e) => e.stopPropagation()}
        className={`relative w-full ${maxWidth} border rounded-t-2xl rounded-b-none sm:rounded-xl shadow-2xl outline-none ${shellClasses} ${className}`}
        style={{ background: 'var(--color-card, #FFFFFF)', borderColor: 'var(--color-border, #E5E7EB)', ...estiloShell }}
      >
        {/* La manija es decorativa (la hoja no se arrastra): con el teclado
            abierto son 18 px de alto que le faltan al contenido, así que se
            esconde. */}
        {!corto && (
          <div className="sm:hidden pt-1.5 pb-1 flex justify-center flex-shrink-0" aria-hidden="true">
            <div className="w-10 h-1 rounded-full" style={{ background: 'var(--color-border, #E5E7EB)' }} />
          </div>
        )}
        {title ? (
          <>
            {/* En teléfono el alto del encabezado lo fija el botón de cerrar
                (44 px táctiles), no el padding: py-1 en vez de py-3 baja el
                bloque de 68 a 52 px sin achicar el objetivo táctil. El título
                va en una sola línea con puntos suspensivos para que un texto
                largo no lo vuelva a levantar a dos o tres renglones. */}
            <div className={`flex items-center justify-between gap-3 px-[16px] sm:px-[20px] ${corto ? 'py-0.5' : 'py-1'} sm:py-3 border-b flex-shrink-0`} style={{ borderColor: 'var(--color-border, #E5E7EB)' }}>
              <h2 id={titleId} className="text-base font-semibold flex-1 min-w-0 truncate" title={typeof title === 'string' ? title : undefined} style={{ color: 'var(--color-foreground, #14201A)' }}>{title}</h2>
              {(dismissible || onClose) && (
                <button
                  type="button" onClick={() => onClose?.()} aria-label="Cerrar"
                  className="w-11 h-11 flex items-center justify-center rounded-lg hover:bg-black/5 flex-shrink-0"
                  style={{ color: 'var(--color-muted-foreground, #6B7A70)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <div
              className="flex-1 overflow-y-auto px-[16px] sm:px-[20px] py-3 sm:py-4"
              style={!footer ? { paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' } : undefined}
            >
              {children}
            </div>
            {footer && (
              <div
                className="flex items-center justify-end gap-2 px-[16px] sm:px-[20px] pt-3 border-t flex-shrink-0"
                style={{ borderColor: 'var(--color-border, #E5E7EB)', paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 14px)' }}
              >
                {footer}
              </div>
            )}
          </>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
